import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { usePageTitle } from "@/lib/title";
import type { Question, Quiz, QuizAttempt } from "@/types";
import { Card, CardBody } from "@/components/ui/Card";
import { Badge, statusTone } from "@/components/ui/Badge";
import { FullPageSpinner } from "@/components/ui/Spinner";
import { EmptyState } from "@/components/ui/EmptyState";
import { ClipboardList } from "lucide-react";

export function QuizResultsPage() {
  const { id } = useParams<{ id: string }>();

  const { data, isLoading: loadingQuiz } = useQuery({
    queryKey: ["quizzes", id],
    queryFn: () => api.get<{ quiz: Quiz; questions: Question[] }>(`/api/v1.0/quizzes/${id}`),
    enabled: !!id,
  });

  const { data: attempts, isLoading: loadingAttempts } = useQuery({
    queryKey: ["quizzes", id, "attempts"],
    queryFn: () => api.get<QuizAttempt[]>(`/api/v1.0/quizzes/${id}/attempts`),
    enabled: !!id,
  });

  usePageTitle(data?.quiz?.title ? `${data.quiz.title} — Results` : "Quiz Results");

  if (loadingQuiz || loadingAttempts || !data) return <FullPageSpinner />;

  return (
    <div className="flex flex-col gap-6">
      <p className="-mt-3 text-sm text-slate-500">{data.questions.length} questions · Passing score {data.quiz.passing_score}%</p>

      {!attempts?.length ? (
        <EmptyState icon={ClipboardList} title="No attempts yet" description="Results will appear here once students take this quiz." />
      ) : (
        <Card>
          <CardBody className="p-0">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-100 text-left text-xs uppercase text-slate-400">
                <tr>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3">Score</th>
                  <th className="px-5 py-3">Result</th>
                  <th className="px-5 py-3">Started</th>
                  <th className="px-5 py-3">Submitted</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {attempts.map((a) => (
                  <tr key={a.id}>
                    <td className="px-5 py-3">
                      <Badge tone={statusTone(a.status)}>{a.status}</Badge>
                    </td>
                    <td className="px-5 py-3">{a.score != null ? `${a.score}%` : "—"}</td>
                    <td className="px-5 py-3">
                      {a.passed == null ? "—" : a.passed ? <Badge tone="green">Passed</Badge> : <Badge tone="red">Failed</Badge>}
                    </td>
                    <td className="px-5 py-3 text-slate-500">{new Date(a.started_at).toLocaleString()}</td>
                    <td className="px-5 py-3 text-slate-500">{a.submitted_at ? new Date(a.submitted_at).toLocaleString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardBody>
        </Card>
      )}
    </div>
  );
}
